import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash, ShieldCheck, Eye, PencilSimple } from "@phosphor-icons/react";

const ROLE_LABELS = { project_manager: "Project Manager", admin: "Admin", member: "Member" };
const ROLE_COLORS = { project_manager: "bg-[#0055FF]/10 text-[#0055FF]", admin: "bg-[#7C3AED]/10 text-[#7C3AED]", member: "bg-[#F5F5F5] text-[#525252]" };

export default function ProjectTeamPanel({ projectId, team, permissions, onRefresh, userType, projectPm }) {
  const [showAdd, setShowAdd] = useState(false);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addForm, setAddForm] = useState({ user_id: "", can_create: false, can_read: true, can_update: false, role: "member" });
  const canManage = permissions?.is_pm || permissions?.is_admin;
  const isClient = userType === "client";
  const internalMembers = team.filter(m => m.user_type === "internal");
  const clientMembers = team.filter(m => m.user_type === "client");

  const loadAvailable = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/projects/${projectId}/available-members`);
      setAvailable(res.data);
    } catch { toast.error("Failed to load available members"); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!addForm.user_id) return toast.error("Select a team member");
    try {
      await api.post(`/projects/${projectId}/team`, addForm);
      toast.success("Team member added");
      setShowAdd(false);
      setAddForm({ user_id: "", can_create: false, can_read: true, can_update: false, role: "member" });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add member"); }
  };

  const handleRemove = async (userId, name) => {
    if (!confirm(`Remove ${name} from this project?`)) return;
    try {
      await api.delete(`/projects/${projectId}/team/${userId}`);
      toast.success(`${name} removed`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to remove member"); }
  };

  const togglePermission = async (userId, field, currentVal) => {
    try {
      await api.put(`/projects/${projectId}/team/${userId}`, { [field]: !currentVal });
      toast.success("Permission updated");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  return (
    <div data-testid="project-team-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Project Team</h3>
        {canManage && (
          <Button variant="outline" size="sm" onClick={() => { setShowAdd(true); loadAvailable(); }}
            className="rounded-none h-7 text-[10px] font-bold uppercase tracking-wider border-[#E5E5E5]"
            data-testid="add-team-member-btn">
            <UserPlus size={12} className="mr-1" /> Add Member
          </Button>
        )}
      </div>

      {/* Eyvo Team - clients only see PM */}
      {isClient ? (
        projectPm && (
          <div className="mb-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] mb-2">Your Eyvo Project Manager</p>
            <div className="flex items-center gap-3 border border-[#E5E5E5] p-3" data-testid="team-pm">
              <div className="w-8 h-8 bg-[#0055FF]/10 flex items-center justify-center text-xs font-bold text-[#0055FF] flex-shrink-0">
                {projectPm.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0A0A0A] truncate">{projectPm.name}</p>
                <p className="text-[10px] text-[#A3A3A3]">{projectPm.email}</p>
              </div>
              <Badge className="text-[9px] font-bold uppercase rounded-none border-0 px-2 py-0.5 bg-[#0055FF]/10 text-[#0055FF]">Project Manager</Badge>
            </div>
          </div>
        )
      ) : internalMembers.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] mb-2">Eyvo Team</p>
          <div className="space-y-2">
            {internalMembers.map(m => (
              <div key={m.id} className="flex items-center gap-3 border border-[#E5E5E5] p-3" data-testid={`team-member-${m.id}`}>
                <div className="w-8 h-8 bg-[#F5F5F5] flex items-center justify-center text-xs font-bold text-[#525252] flex-shrink-0">
                  {m.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate">{m.name}</p>
                  <p className="text-[10px] text-[#A3A3A3]">{m.email}</p>
                </div>
                <Badge className={`text-[9px] font-bold uppercase rounded-none border-0 px-2 py-0.5 ${ROLE_COLORS[m.project_role] || ROLE_COLORS.member}`}>
                  {ROLE_LABELS[m.project_role] || m.project_role || "Member"}
                </Badge>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => togglePermission(m.id, "can_create", m.can_create)}
                      className={`p-1 rounded-sm text-[10px] font-bold ${m.can_create ? "bg-[#16A34A]/10 text-[#16A34A]" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}
                      title={m.can_create ? "Can Create (click to revoke)" : "No Create (click to grant)"}
                      data-testid={`perm-create-${m.id}`}>C</button>
                    <button onClick={() => togglePermission(m.id, "can_read", m.can_read)}
                      className={`p-1 rounded-sm text-[10px] font-bold ${m.can_read ? "bg-[#0055FF]/10 text-[#0055FF]" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}
                      title={m.can_read ? "Can Read (click to revoke)" : "No Read (click to grant)"}
                      data-testid={`perm-read-${m.id}`}>R</button>
                    <button onClick={() => togglePermission(m.id, "can_update", m.can_update)}
                      className={`p-1 rounded-sm text-[10px] font-bold ${m.can_update ? "bg-[#D97706]/10 text-[#D97706]" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}
                      title={m.can_update ? "Can Update (click to revoke)" : "No Update (click to grant)"}
                      data-testid={`perm-update-${m.id}`}>U</button>
                    {m.project_role !== "project_manager" && (
                      <button onClick={() => handleRemove(m.id, m.name)} className="p-1 hover:bg-[#FEF2F2] rounded-sm" title="Remove from project"
                        data-testid={`remove-member-${m.id}`}>
                        <Trash size={14} className="text-[#DC2626]" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client Team */}
      {clientMembers.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-2">Client Team</p>
          <div className="space-y-2">
            {clientMembers.map(m => (
              <div key={m.id} className="flex items-center gap-3 border border-[#E5E5E5] p-3" data-testid={`team-member-${m.id}`}>
                <div className="w-8 h-8 bg-[#F5F5F5] flex items-center justify-center text-xs font-bold text-[#525252] flex-shrink-0">
                  {m.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate">{m.name}</p>
                  <p className="text-[10px] text-[#A3A3A3]">{m.email}</p>
                </div>
                <Badge className="text-[9px] font-bold uppercase rounded-none border-0 px-2 py-0.5 bg-[#F5F5F5] text-[#525252]">Client</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {internalMembers.length === 0 && clientMembers.length === 0 && (
        <p className="text-sm text-[#A3A3A3] text-center py-4">No team members assigned yet.</p>
      )}

      {/* Add Member Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-md" data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252] block mb-1.5">Select Member</label>
              <select value={addForm.user_id} onChange={e => setAddForm({ ...addForm, user_id: e.target.value })}
                className="w-full h-10 px-3 text-sm border border-[#E5E5E5] bg-white" data-testid="select-member">
                <option value="">-- Choose team member --</option>
                {available.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252] block mb-1.5">Permissions</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={addForm.can_read} onChange={e => setAddForm({ ...addForm, can_read: e.target.checked })} />
                  <Eye size={14} className="text-[#0055FF]" /> Read
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={addForm.can_create} onChange={e => setAddForm({ ...addForm, can_create: e.target.checked })} />
                  <PencilSimple size={14} className="text-[#16A34A]" /> Create
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={addForm.can_update} onChange={e => setAddForm({ ...addForm, can_update: e.target.checked })} />
                  <ShieldCheck size={14} className="text-[#D97706]" /> Update
                </label>
              </div>
            </div>
            <Button onClick={handleAdd} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10" data-testid="confirm-add-member">
              Add to Project
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
